import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  DollarSign, 
  Clock, 
  Users, 
  Shield,
  TrendingDown,
  Send,
  CheckCircle,
  XCircle,
  Coins
} from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { useSubscription } from '@/components/SubscriptionBanner';
import { formatCurrency, formatDate, getStatusColor } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RFBDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { isSubscribed } = useSubscription();
  const [rfb, setRfb] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [bidLoading, setBidLoading] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(null);
  const [bidForm, setBidForm] = useState({
    interest_rate: '',
    terms: '',
    stablecoin: 'USDC'
  });

  const stablecoins = [
    { value: 'USD', label: 'USD' },
    { value: 'USDC', label: 'USDC' },
    { value: 'USDT', label: 'USDT' },
    { value: 'DAI', label: 'DAI' },
    { value: 'USAT', label: 'USAT' },
  ];

  const isConsumer = user?.role === 'consumer';
  const isLender = user?.role === 'lender';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const rfbRes = await axios.get(`${API}/loans/requests/${id}`);
        setRfb(rfbRes.data);

        // Fetch bids if consumer (owner of request)
        if (isConsumer) {
          const bidsRes = await axios.get(`${API}/bids?loan_request_id=${id}`);
          setBids(bidsRes.data);
        }
      } catch (err) {
        console.error('Failed to fetch RFB:', err);
        toast({
          title: 'Error',
          description: 'Failed to load loan request',
          variant: 'destructive'
        });
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isConsumer, navigate, toast]);

  const handlePlaceBid = async () => {
    if (!bidForm.interest_rate || parseFloat(bidForm.interest_rate) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid interest rate', variant: 'destructive' });
      return;
    }

    setBidLoading(true);
    try {
      await axios.post(`${API}/bids`, {
        loan_request_id: id,
        interest_rate: parseFloat(bidForm.interest_rate),
        stablecoin: bidForm.stablecoin,
        terms: bidForm.terms || null
      });

      toast({
        title: 'Bid Placed!',
        description: 'Your bid has been submitted successfully.'
      });
      
      setBidDialogOpen(false);
      setBidForm({ interest_rate: '', terms: '', stablecoin: 'USDC' });
      
      // Refresh data
      const rfbRes = await axios.get(`${API}/loans/requests/${id}`);
      setRfb(rfbRes.data);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to place bid',
        variant: 'destructive'
      });
    } finally {
      setBidLoading(false);
    }
  };

  const handleAcceptBid = async (bidId) => {
    setAcceptLoading(bidId);
    try {
      await axios.post(`${API}/bids/${bidId}/accept`);
      
      toast({
        title: 'Bid Accepted!',
        description: 'The loan has been funded. Check your loans page for details.'
      });
      
      navigate('/consumer');
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to accept bid',
        variant: 'destructive'
      });
    } finally {
      setAcceptLoading(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Loan Request Details">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!rfb) return null;

  return (
    <DashboardLayout title="Loan Request Details">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Back
        </Button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-3xl font-bold gradient-text mb-2">
                      {formatCurrency(rfb.amount)}
                    </h2>
                    <p className="text-lg text-muted-foreground">{rfb.purpose}</p>
                  </div>
                  <Badge className={`${getStatusColor(rfb.status)} text-lg px-3 py-1`}>
                    {rfb.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-white/5">
                    <Clock className="w-5 h-5 text-muted-foreground mb-2" />
                    <div className="text-sm text-muted-foreground">Term</div>
                    <div className="font-semibold">{rfb.term_months} months</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5">
                    <Users className="w-5 h-5 text-muted-foreground mb-2" />
                    <div className="text-sm text-muted-foreground">Bids</div>
                    <div className="font-semibold">{rfb.bids_count}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5">
                    <TrendingDown className="w-5 h-5 text-green-500 mb-2" />
                    <div className="text-sm text-muted-foreground">Best Rate</div>
                    <div className="font-semibold text-green-500">
                      {rfb.best_rate ? `${rfb.best_rate}%` : 'No bids'}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5">
                    <Shield className="w-5 h-5 text-muted-foreground mb-2" />
                    <div className="text-sm text-muted-foreground">Collateral</div>
                    <div className="font-semibold">
                      {rfb.collateral_type || 'None'}
                    </div>
                  </div>
                  {rfb.preferred_stablecoin && (
                    <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/10">
                      <Coins className="w-5 h-5 text-blue-400 mb-2" />
                      <div className="text-sm text-muted-foreground">Preferred Coin</div>
                      <div className="font-semibold text-blue-400">{rfb.preferred_stablecoin}</div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5">Requested by: {rfb.consumer_name} {rfb.kyc_verified && <VerifiedBadge />}</span>
                  <span>•</span>
                  <span>{formatDate(rfb.created_at)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Bids Section (Consumer Only) */}
            {isConsumer && rfb.status === 'pending' && (
              <Card className="glass border-white/10">
                <CardHeader>
                  <CardTitle>Received Bids</CardTitle>
                </CardHeader>
                <CardContent>
                  {bids.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No bids received yet</p>
                      <p className="text-sm">Lenders will submit bids soon</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {bids.sort((a, b) => a.interest_rate - b.interest_rate).map((bid) => (
                        <motion.div
                          key={bid.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10"
                        >
                          <div>
                            <div className="text-xl font-bold text-primary">
                              {bid.interest_rate}% APR
                              <span className="ml-2 text-sm font-medium px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                {bid.stablecoin || 'USDC'}
                              </span>
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1.5">
                              by {bid.lender_name} {bid.kyc_verified && <VerifiedBadge />} • {formatDate(bid.created_at)}
                            </div>
                            {bid.terms && (
                              <p className="text-sm mt-2">{bid.terms}</p>
                            )}
                          </div>
                          {bid.status === 'pending' && (
                            <Button
                              data-testid={`accept-bid-${bid.id}`}
                              onClick={() => handleAcceptBid(bid.id)}
                              disabled={acceptLoading === bid.id}
                              className="gradient-primary"
                            >
                              {acceptLoading === bid.id ? (
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="mr-2 w-4 h-4" />
                                  Accept
                                </>
                              )}
                            </Button>
                          )}
                          {bid.status !== 'pending' && (
                            <Badge className={getStatusColor(bid.status)}>
                              {bid.status}
                            </Badge>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Place Bid (Lender Only) */}
            {isLender && rfb.status === 'pending' && (
              <Card className="glass border-white/10">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Place Your Bid</h3>
                  {isSubscribed ? (
                    <>
                      <p className="text-sm text-muted-foreground mb-4">
                        Compete with other lenders by offering your best interest rate.
                      </p>
                      <Button
                        data-testid="place-bid-btn"
                        onClick={() => setBidDialogOpen(true)}
                        className="w-full gradient-primary"
                      >
                        <Send className="mr-2 w-4 h-4" />
                        Place Bid
                      </Button>
                    </>
                  ) : (
                    <div data-testid="bid-subscription-gate">
                      <p className="text-sm text-orange-300/80 mb-3">
                        An active subscription is required to place bids.
                      </p>
                      <Button
                        data-testid="subscribe-to-bid-btn"
                        onClick={() => navigate('/lender')}
                        className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:brightness-110"
                      >
                        Subscribe for $55/month to Bid
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Loan Calculator */}
            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">Loan Calculator</h3>
                {rfb.best_rate ? (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Principal</span>
                      <span>{formatCurrency(rfb.amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Best Rate</span>
                      <span>{rfb.best_rate}% APR</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Term</span>
                      <span>{rfb.term_months} months</span>
                    </div>
                    <div className="border-t border-white/10 pt-3">
                      <div className="flex justify-between font-semibold">
                        <span>Est. Monthly</span>
                        <span className="text-primary">
                          {formatCurrency(
                            (rfb.amount * (1 + (rfb.best_rate / 100) * (rfb.term_months / 12))) / rfb.term_months
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center">
                    Waiting for bids to calculate estimates
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Bid Dialog */}
      <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
        <DialogContent className="glass border-white/10">
          <DialogHeader>
            <DialogTitle>Place Your Bid</DialogTitle>
            <DialogDescription>
              Offer your best interest rate for this loan request of {formatCurrency(rfb.amount)}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Interest Rate (% APR)</Label>
              <Input
                data-testid="bid-rate-input"
                type="number"
                placeholder="e.g., 12.5"
                value={bidForm.interest_rate}
                onChange={(e) => setBidForm({ ...bidForm, interest_rate: e.target.value })}
                className="mt-1.5 bg-white/5 border-white/10"
                step="0.1"
                min="0.1"
                max="100"
              />
            </div>
            <div>
              <Label>Settlement Currency</Label>
              <Select 
                value={bidForm.stablecoin} 
                onValueChange={(v) => setBidForm({ ...bidForm, stablecoin: v })}
              >
                <SelectTrigger data-testid="bid-stablecoin-select" className="mt-1.5 bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stablecoins.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Choose which currency you'll fund this loan with.</p>
            </div>
            <div>
              <Label>Additional Terms (Optional)</Label>
              <Textarea
                data-testid="bid-terms-input"
                placeholder="Any special terms or conditions..."
                value={bidForm.terms}
                onChange={(e) => setBidForm({ ...bidForm, terms: e.target.value })}
                className="mt-1.5 bg-white/5 border-white/10"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBidDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="submit-bid-btn"
              onClick={handlePlaceBid}
              disabled={bidLoading}
              className="gradient-primary"
            >
              {bidLoading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Submit Bid'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
